import React, { useState } from "react";
import { OfferFilterV2 } from "../../../../types/Transaction";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { HStack, VStack } from "../../../../components/utils";
import { Button, ChevronDown, Search, X } from "./ComponentUtils";
import { Input } from "../../../../components/shared/InputField";

export interface FilterType {
  id: string;
  label: string;
  options?: string[];
  type: string;
}

export function MultiCheckBoxPopOver({
  index,
  filter,
  formik,
  disableUpdate = false,
}: {
  index: number;
  filter: FilterType;
  formik: any;
  disableUpdate: boolean;
}) {
  const [searchValues, setSearchValues] = useState<{ [key: string]: string }>(
    {}
  );
  const isFilterActive = (filterId: string, filterType: string): boolean => {
    const filterValue =
      formik.values.offerFilter[filterId as keyof OfferFilterV2];

    if (filterType === "multiCheckbox") {
      return Array.isArray(filterValue) && filterValue.length > 0;
    }

    return false;
  };
  const [tempSelectedOptions, setTempSelectedOptions] = useState<{
    [key: string]: string[];
  }>(() => {
    const initialOptions: { [key: string]: string[] } = {};

    Object.keys(formik.values.offerFilter).forEach((key) => {
      if (
        Array.isArray(formik.values.offerFilter[key as keyof OfferFilterV2])
      ) {
        initialOptions[key] = [
          ...(formik.values.offerFilter[
            key as keyof OfferFilterV2
          ] as string[]),
        ];
      }
    });

    return initialOptions;
  });
  return (
    <Popover
      key={index}
      content={
        <PopoverContent className="w-60">
          <VStack className="gap-3">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search"
                className="pl-10"
                value={searchValues[filter.id] || ""}
                onChange={(e) => {
                  const searchValue = e.target.value;
                  setSearchValues((prev) => ({
                    ...prev,
                    [filter.id]: searchValue,
                  }));
                }}
              />
            </div>

            {/* Multi-Checkbox Options */}
            <VStack className="gap-2 overflow-y-auto max-h-60">
              {filter.options
                ?.filter((option) =>
                  option
                    ?.toLowerCase()
                    .includes((searchValues[filter.id] || "").toLowerCase())
                )
                .map((option, optIdx) => (
                  <HStack key={optIdx} className="justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(tempSelectedOptions[filter.id]) &&
                          (tempSelectedOptions[filter.id] as string[]).includes(
                            option
                          )
                        }
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setTempSelectedOptions((prev) => {
                            const currentValues = Array.isArray(prev[filter.id])
                              ? (prev[filter.id] as string[])
                              : [];
                            return {
                              ...prev,
                              [filter.id]: isChecked
                                ? [...currentValues, option]
                                : currentValues.filter((val) => val !== option),
                            };
                          });
                        }}
                      />
                      {option}
                    </label>
                  </HStack>
                ))}
            </VStack>

            {/* Clear and Apply Buttons */}
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    formik.setFieldValue(`offerFilter.${filter.id}`, []); // Clear filter in Formik

                    setSearchValues((prev) => ({
                      ...prev,
                      [filter.id]: "",
                    }));
                    setTempSelectedOptions((prev) => ({
                      ...prev,
                      [filter.id]: [],
                    }));
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="text-white bg-orange-500 hover:bg-orange-600"
                  variant="primary"
                  onClick={() => {
                    formik.setFieldValue(
                      `offerFilter.${filter.id}`,
                      tempSelectedOptions[filter.id] || []
                    );
                  }}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isFilterActive(filter.id, filter.type)
              ? "bg-[#003366] text-white hover:bg-[#003366]"
              : ""
          }`}
        >
          {filter.label}
          {isFilterActive(filter.id, filter.type) &&
            (
              formik.values.offerFilter[
                filter.id as keyof OfferFilterV2
              ] as string[]
            ).length > 0 && (
              <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
                {
                  (
                    formik.values.offerFilter[
                      filter.id as keyof OfferFilterV2
                    ] as string[]
                  )?.length
                }
              </span>
            )}
          {(
            formik.values.offerFilter[
              filter.id as keyof OfferFilterV2
            ] as string[]
          )?.length > 0 && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                formik.setFieldValue(`offerFilter.${filter.id}`, []); // Clear filter in Formik

                setSearchValues((prev) => ({
                  ...prev,
                  [filter.id]: "",
                }));
                setTempSelectedOptions((prev) => ({
                  ...prev,
                  [filter.id]: [],
                }));
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}
